import React, { useState, useEffect } from "react";
import api from "../utils/axiosConfig";
import localforage from "localforage";
import Dashboard from "./Dashboard";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import {
  zoneRolesAp,
  parliamentaryConstituencyRolesAp,
  assemblyConstituenciesAp,
} from "./Roles";
import { fetchGeoHierarchy } from "../utils/geo";
import punjabGeoData from "../data/punjabGeography.json";
import "../css/travelForm.css";
import "../css/cabForm.css";

const API_URL = process.env.REACT_APP_API_URL;

// Punjab cab requests are recorded at Region + District level only — Punjab
// teams do not work to PC/AC, so the Assembly Constituency field is not shown.
const PJ_REGIONS = Object.keys(punjabGeoData.regions || {}).sort();
const PJ_DISTRICTS_BY_REGION = Object.entries(punjabGeoData.regions || {}).reduce(
  (acc, [regionName, r]) => {
    acc[regionName] = Object.keys(r.districts || {}).sort();
    return acc;
  },
  {}
);
const TEAM_OPTIONS = ["DMC", "Soul central", "PMU", "Field", "Director's Team", "Campaign"];
const CAB_DESIGNATIONS = [
  "PCM",
  "Zonal Manager",
  "State Lead",
  "Campaign Manager",
  "Campaign Team",
  "DMC Team",
  "Director's Team",
  "Campaign",
];
const phoneDigits = (p) => (p || "").replace(/\D/g, "").slice(-10);

const CabForm = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [cabRequests, setCabRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedAddOnPersons, setSelectedAddOnPersons] = useState([]);
  const [locating, setLocating] = useState(false);
  const [mapLink, setMapLink] = useState("");
  const [formData, setFormData] = useState({
    userId: "",
    email: "",
    dateOfRequest: "",
    employeePhoneNumber: "",
    pickupTime: "",
    pickupLocation: "",
    purpose: "",
    state: "",
    zone: "",
    pcName: "",
    acName: "",
    team: "",
    designation: "",
    phoneNumber: "",
    approxDriveKms: "",
    addOnPerson: [],
  });
  const [geoTree, setGeoTree] = useState({});

  useEffect(() => {
    fetchGeoHierarchy().then(setGeoTree);
  }, []);


  
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userId = await localforage.getItem("ID");
        const email = await localforage.getItem("email");
        const location = await localforage.getItem("location");
        if (userId && email) {
          setFormData((prevData) => ({
            ...prevData,
            userId,
            email,
            state: prevData.state || location || "",
          }));
          fetchCabRequests(userId);
        } else {
          setError("User ID or Email not found. Please log in again.");
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
      }
    };
    fetchUserData();
    fetchEmployees();
  }, []);

  const fetchCabRequests = async (userId) => {
    try {
      const token = await localforage.getItem("token");
      const response = await api.get(
        `${API_URL}/cab/cab-requests`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setCabRequests(response.data.cabRequests);
    } catch (error) {
      console.error("Error fetching cab requests:", error);
      setError("Failed to load cab requests.");
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = await localforage.getItem("token");
      const response = await api.get(
        `${API_URL}/cab/employees/all`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddOnChange = (selectedOptions) => {
    setSelectedAddOnPersons(selectedOptions);
    setFormData({
      ...formData,
      addOnPerson: selectedOptions.map((option) => ({
        employeeName: option.label,
        employeePhoneNumber: option.phone,
        employeeEmail: option.email,
      })),
    });
  };

  // Capture the user's current GPS position and attach it as a
  // Google Maps link to the pickup location.
  const handleCaptureLocation = () => {
    if (!navigator.geolocation) {
      alert("Location is not supported on this browser.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const link = `https://www.google.com/maps?q=${latitude},${longitude}`;
        setMapLink(link);
        setFormData((prev) => {
          // strip any previously attached map link so repeated
          // captures don't stack up
          const base = (prev.pickupLocation || "")
            .replace(
              /\s*\|\s*https:\/\/www\.google\.com\/maps\?q=[-0-9.,]+/g,
              ""
            )
            .trim();
          const combined = base ? `${base} | ${link}` : link;
          return { ...prev, pickupLocation: combined };
        });
        setLocating(false);
      },
      () => {
        alert(
          "Could not get your location. Please allow location access in your browser and try again."
        );
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // --- Submission window (closes 6 PM IST). The SERVER decides; we only
  // reflect it, so a wrong desktop clock cannot open or close the form. ---
  const [windowOpen, setWindowOpen] = useState(true);
  const [windowLabel, setWindowLabel] = useState("6:00 PM");

  useEffect(() => {
    let cancelled = false;
    const checkWindow = async () => {
      try {
        const token = await localforage.getItem("token");
        const { data } = await api.get(`${API_URL}/cab/cab-window`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled || !data) return;
        setWindowOpen(!!data.open);
        if (data.cutoffLabel) setWindowLabel(data.cutoffLabel);
      } catch (e) {
        // If the check fails, leave the form usable — the server still
        // rejects late submissions, so nothing slips through.
      }
    };
    checkWindow();
    const id = setInterval(checkWindow, 60000); // re-check every minute
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!windowOpen) {
      setError(
        `Cab requests are closed for today. Requests can only be raised before ${windowLabel}.`
      );
      return;
    }

    // Phone must be exactly 10 digits (stored as +91-XXXXXXXXXX)
    if (phoneDigits(formData.phoneNumber).length !== 10) {
      setError("Phone Number must be exactly 10 digits (e.g. +91-7854526589).");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const token = await localforage.getItem("token");
      if (!formData.userId) {
        throw new Error("Missing userId in the request body");
      }
      await api.post(
        `${API_URL}/cab/cab-record`,
        {
          ...formData,
          phoneNumber: formData.phoneNumber ? "+91-" + formData.phoneNumber : "",
          pickupMapLink: mapLink,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      alert("Cab request submitted!");
      setFormData({
        userId: formData.userId,
        email: formData.email,
        dateOfRequest: "",
        employeePhoneNumber: "",
        pickupTime: "",
        pickupLocation: "",
        purpose: "",
        state: formData.state,
        zone: "",
        pcName: "",
        acName: "",
        team: "",
        designation: "",
        phoneNumber: "",
        approxDriveKms: "",
        addOnPerson: [],
      });
      setSelectedAddOnPersons([]);
      setMapLink("");
      fetchCabRequests(formData.userId);
    } catch (error) {
      console.error("Error submitting request:", error);
      if (error.response && error.response.data && error.response.data.error) {
        setError(error.response.data.error);
      } else {
        setError("Failed to submit request.");
      }
    } finally {
      setLoading(false);
    }
  };


  const handleDeleteIntervention = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this record?"
    );
    if (!confirmDelete) return;

    try {
      const token = await localforage.getItem("token");
      const response = await api.delete(
        `${API_URL}/cab/delete-cab-request/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.status === 200) {
        alert("Record deleted successfully!");
        setCabRequests((prevData) =>
          prevData.filter((data) => data._id !== id)
        );
      }
    } catch (error) {
      alert(
        error.response?.data?.error ||
          "Failed to delete record. Please try again."
      );
    }
  };

  return (
    <>
      <Dashboard />
      <div className="cab-timing-banner">
        Cab requests will be allowed only between 9:00 AM and 6:00 PM
      </div>
      <div className="cab-modern-card">
        <div className="cab-card-head">
          <h2>Cab Request Form</h2>
          <p>Fill in the details below to request a cab.</p>
        </div>
        {success && <p className="success-message">{success}</p>}
        {error && <p className="error-message">{error}</p>}
        {!windowOpen && (
          <p
            className="error-message"
            style={{
              background: "#fff4f4",
              border: "1px solid #f3c9c9",
              borderRadius: "10px",
              padding: "12px 14px",
              color: "#a12b2b",
              fontWeight: 600,
            }}
          >
            Cab requests are closed for today. Requests can only be raised
            before {windowLabel}. Please submit tomorrow.
          </p>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <label>
              Date of Request:
              <input
                type="date"
                name="dateOfRequest"
                onChange={handleChange}
                value={formData.dateOfRequest}
                required
                min={new Date().toISOString().split("T")[0]} // Prevent past dates
              />
            </label>
            <label>
              Purpose:
              <input
                type="text"
                name="purpose"
                placeholder="Purpose"
                onChange={handleChange}
                value={formData.purpose}
                required
              />
            </label>
          </div>
          <div className="form-row">
            <label>
              Pickup Time:
              <input
                type="time"
                name="pickupTime"
                onChange={handleChange}
                value={formData.pickupTime}
                required
                min={
                  formData.dateOfRequest ===
                  new Date().toISOString().split("T")[0]
                    ? new Date()
                        .toLocaleTimeString("en-GB", { hour12: false })
                        .slice(0, 5) // Blocks past times if today is selected
                    : ""
                }
              />
            </label>
            <label>
              Pickup Location:
              <input
                type="text"
                name="pickupLocation"
                placeholder="Type address or capture current location"
                onChange={handleChange}
                value={formData.pickupLocation}
                required
              />
              <button
                type="button"
                className="capture-location-btn"
                onClick={handleCaptureLocation}
                disabled={locating}
              >
                {locating ? "Getting location..." : "📍 Use current location"}
              </button>
              {mapLink && (
                <a
                  className="map-preview-link"
                  href={mapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Location captured — view on Google Maps
                </a>
              )}
            </label>
          </div>
          <div className="form-row">
            <label>
              State:
              <select
                name="state"
                value={formData.state}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    state: e.target.value,
                    zone: "",
                    pcName: "",
                    acName: "",
                  })
                }
              >
                <option value="">Select State</option>
                {["Andhra Pradesh", "Punjab"].map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {formData.state === "Punjab" ? (
            <div className="form-row">
              <label>
                Region:
                <select
                  name="zone"
                  value={formData.zone}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      zone: e.target.value,
                      pcName: "",
                      acName: "",
                    }))
                  }
                >
                  <option value="">Select Region</option>
                  {PJ_REGIONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                District:
                <select
                  name="pcName"
                  value={formData.pcName}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      pcName: e.target.value,
                      acName: "",
                    }))
                  }
                  disabled={!formData.zone}
                >
                  <option value="">
                    {formData.zone ? "Select District" : "Select Region first"}
                  </option>
                  {(PJ_DISTRICTS_BY_REGION[formData.zone] || []).map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : (
            <div className="form-row">
            <label>
              Zone:
              <select
                name="zone"
                value={formData.zone}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    zone: e.target.value,
                    pcName: "",
                    acName: "",
                  })
                }
              >
                <option value="">Select Zone</option>
                {(Object.keys(geoTree).length
                  ? Object.keys(geoTree).sort()
                  : zoneRolesAp
                ).map((z) => (
                  <option key={z} value={z}>
                    {z}
                  </option>
                ))}
              </select>
            </label>
            <label>
              PC Name:
              <select
                name="pcName"
                value={formData.pcName}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    pcName: e.target.value,
                    acName: "",
                  })
                }
                disabled={!!Object.keys(geoTree).length && !formData.zone}
              >
                <option value="">
                  {Object.keys(geoTree).length && !formData.zone
                    ? "Select Zone first"
                    : "Select PC"}
                </option>
                {(Object.keys(geoTree).length
                  ? Object.keys(geoTree[formData.zone] || {}).sort()
                  : parliamentaryConstituencyRolesAp
                ).map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
            <label>
              AC Name:
              <select
                name="acName"
                value={formData.acName}
                onChange={handleChange}
                disabled={!!Object.keys(geoTree).length && !formData.pcName}
              >
                <option value="">
                  {Object.keys(geoTree).length && !formData.pcName
                    ? "Select PC first"
                    : "Select AC"}
                </option>
                {(Object.keys(geoTree).length
                  ? (geoTree[formData.zone] || {})[formData.pcName] || []
                  : assemblyConstituenciesAp
                ).map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </label>
          </div>
          )}
          <div className="form-row">
            <label>
              Team:
              <select name="team" value={formData.team} onChange={handleChange}>
                <option value="">Select Team</option>
                {TEAM_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Designation:
              <select
                name="designation"
                value={formData.designation}
                onChange={handleChange}
              >
                <option value="">Select Designation</option>
                {CAB_DESIGNATIONS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="form-row">
            <label>
              Phone Number:
              <span className="phone-wrap">
                <span className="phone-prefix">+91-</span>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  value={phoneDigits(formData.phoneNumber)}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      phoneNumber: phoneDigits(e.target.value),
                    })
                  }
                  placeholder="Enter 10-digit number"
                />
              </span>
              <small>{phoneDigits(formData.phoneNumber).length}/10 digits</small>
            </label>
            <label>
              Approx Drive Kms:
              <input
                type="text"
                name="approxDriveKms"
                value={formData.approxDriveKms}
                onChange={handleChange}
                placeholder="e.g. 25 km"
              />
            </label>
          </div>
          <div className="form-row">
            <label>
              Add-On Persons:
              <Select
                options={employees.map((emp) => ({
                  label: emp.employeeName,
                  value: emp.employeeEmail,
                  phone: emp.employeePhoneNumber,
                  email: emp.employeeEmail,
                }))}
                isMulti
                value={selectedAddOnPersons}
                onChange={handleAddOnChange}
                maxMenuHeight={150}
                placeholder="Select up to 3 employees"
                isSearchable
                closeMenuOnSelect={false}
                isOptionDisabled={() => selectedAddOnPersons.length >= 3}
              />
            </label>
          </div>
          <div className="mailto3">
            <button type="submit" disabled={loading || !windowOpen}>
              {loading ? "Requesting a Cab..." : "Request Cab"}
            </button>
          </div>
        </form>
      </div>
      <div
        className="intervention-table"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <h3>Previous Cab Requests</h3>
        <table>
          <thead>
            <tr>
              <th>Cab Request Code</th>
              <th>Requested By</th>
              <th>Date</th>
              <th>Pickup Time</th>
              <th>Pickup Location</th>
              <th>Purpose</th>
              <th>Cab Number</th>
              <th>Driver Name</th>
              <th>Driver Number</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {cabRequests.length > 0 ? (
              cabRequests.map((request) => (
                <tr key={request._id}>
                  <td style={{ textAlign: "center" }}>
                    {request.cabRequestCode}
                  </td>
                  <td style={{ textAlign: "center" }}>{request.name}</td>
                  <td style={{ textAlign: "center" }}>
                    {request.dateOfRequest}
                  </td>
                  <td style={{ textAlign: "center" }}>{request.pickupTime}</td>
                  <td style={{ textAlign: "center" }}>
                    {request.pickupLocation}
                  </td>
                  <td style={{ textAlign: "center" }}>{request.purpose}</td>
                  <td style={{ textAlign: "center" }}>{request.cabNumber}</td>
                  <td style={{ textAlign: "center" }}>{request.driverName}</td>
                  <td style={{ textAlign: "center" }}>
                    {request.driverNumber}
                  </td>
                  <td>
                    <div className="action-icons-div">
                      {request.cabNumber ? (
                        <Link
                          to={`/update-cab-request/${request._id}`}
                          style={{
                            textDecoration: "none",
                            padding: "5px 0px",
                            color: "#008cff",
                          }}
                        >
                          <FontAwesomeIcon icon={faEdit} size="1x" />
                        </Link>
                      ) : (
                        <span
                          style={{
                            textDecoration: "none",
                            padding: "5px 0px",
                            color: "#ccc",
                            cursor: "not-allowed",
                          }}
                          title="Cannot update until cab number is assigned"
                        >
                          <FontAwesomeIcon icon={faEdit} size="1x" />
                        </span>
                      )}
                      <FontAwesomeIcon
                        onClick={() => handleDeleteIntervention(request._id)}
                        icon={faTrash}
                        size="1x"
                      />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4">No cab requests found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default CabForm;
